import type { RoutePath } from '#routes.js';
import type { IconName } from '#ui/MaterialIcon.js';

import React from 'react';

import { ROUTES } from '@elara/client-core/constants';
import { formatDate } from '@elara/client-core/lib/date-utils';
import { travelPreferencesComplete } from '@elara/client-core/lib/travel-preferences';
import { PRIVACY_URL, TERMS_URL } from '@elara/domain/policy-versions';

import {
  useCurrentUserProfile,
  usePhoneLinkState,
  useRouterState,
  useSessionState,
  useUserProfileState,
} from '#states/hooks.js';
import { Loader, Logo } from '#ui/index.js';
import { IconSizes, MaterialIcon } from '#ui/MaterialIcon.js';
import { AppLayout, AppLayoutTitle } from '#views/layout/AppLayout.jsx';
import { Link } from '#views/Router.jsx';

import { EditableAvatar } from './EditableAvatar.js';
import { MembershipPanel } from './MembershipPanel.js';
import { PaymentFailedAlert } from './PaymentFailedAlert.js';
import { useBillingPortal } from './use-billing-portal.js';

/**
 * Sentinel for the Elara mark in a row's leading slot, where every other row carries a Material
 * symbol. Only the membership row uses it — the membership IS the brand, and no glyph in the set
 * says that as directly.
 */
const ELARA_MARK = 'elara-mark';

type RowIcon = IconName | typeof ELARA_MARK;

/** Row callouts, worded as on the native Account screen. */
const PROFILE_PHONE_CALLOUT = 'Add a phone number to message Elara by SMS.';
const TRAVEL_PREFERENCES_CALLOUT = 'Complete your preferences to get better recommendations from Elara.';

/**
 * A row in the account menu. Three shapes, because a row either goes somewhere in the app, leaves
 * for the web, or does something here — and the trailing glyph is the promise it makes about which.
 */
type AccountRow =
  | { kind: 'link'; to: RoutePath; icon: RowIcon; label: string; callout?: string }
  | { kind: 'external'; href: string; icon: RowIcon; label: string }
  | { kind: 'action'; icon: RowIcon; label: string; trailingIcon?: IconName; disabled?: boolean; onClick: () => void };

const ROW_CLASSES =
  'group flex w-full cursor-pointer items-center gap-4 px-3 -mx-3 py-3 rounded-md text-left hover:bg-background-secondary transition-colors disabled:pointer-events-none disabled:opacity-60';

/**
 * The trailing glyph, which is the row's whole promise: a chevron goes to another screen in the app,
 * `arrow_outward` leaves for somewhere else. Same convention as the native account screen.
 */
function trailingIconFor(row: AccountRow): IconName | undefined {
  if (row.kind === 'link') return 'chevron_right';
  if (row.kind === 'external') return 'arrow_outward';
  return row.trailingIcon;
}

/**
 * The leading glyph. The mark is drawn a little under the 20px icon box and centred inside one,
 * because a Material symbol leaves padding inside its box while the mark fills its whole viewBox —
 * matched on the box, they would not look the same size, and the labels would not start level.
 */
function AccountRowIcon({ icon, badge = false }: { icon: RowIcon; badge?: boolean }) {
  if (badge) {
    // A small destructive dot on the icon's corner: something on that page is incomplete. Same
    // treatment as the native Account screen's `DetailRow` badge.
    return (
      <span className="relative flex shrink-0" data-testid="account-row-badge">
        <AccountRowIcon icon={icon} />
        <span className="absolute -bottom-0.5 -right-0.5 size-2 rounded-full bg-destructive ring-2 ring-background" />
      </span>
    );
  }
  if (icon === ELARA_MARK) {
    return (
      <span className="flex w-5 shrink-0 justify-center text-muted-foreground" aria-hidden>
        <Logo variant="symbol" height={17} className="text-current" />
      </span>
    );
  }
  return <MaterialIcon icon={icon} size={IconSizes.sm} weight="light" className="text-muted-foreground" />;
}

/**
 * One row's contents, shared by all three shapes so they cannot drift apart visually. A link row's
 * `callout` marks something incomplete on that page: a dot on the icon and the reason underneath.
 */
function AccountRowContent({ row }: { row: AccountRow }) {
  const trailingIcon = trailingIconFor(row);
  const callout = row.kind === 'link' ? row.callout : undefined;
  return (
    <>
      <AccountRowIcon icon={row.icon} badge={!!callout} />
      <div className="flex-1 min-w-0">
        {row.label}
        {callout && <p className="text-sm text-muted-foreground">{callout}</p>}
      </div>
      {trailingIcon && (
        <MaterialIcon
          icon={trailingIcon}
          size={IconSizes.sm}
          className={
            trailingIcon === 'progress_activity'
              ? 'animate-spin text-muted-foreground'
              : 'text-muted-foreground/60 group-hover:text-muted-foreground transition-colors'
          }
        />
      )}
    </>
  );
}

function AccountMenuRow({ row }: { row: AccountRow }) {
  if (row.kind === 'link') {
    return (
      <Link to={row.to} className={ROW_CLASSES}>
        <AccountRowContent row={row} />
      </Link>
    );
  }

  if (row.kind === 'external') {
    return (
      <a href={row.href} target="_blank" rel="noopener noreferrer" className={ROW_CLASSES}>
        <AccountRowContent row={row} />
      </a>
    );
  }

  return (
    <button type="button" className={ROW_CLASSES} disabled={row.disabled} onClick={row.onClick}>
      <AccountRowContent row={row} />
    </button>
  );
}

/**
 * Account home. Two columns (ELARA-1014): the settings menu on the left, the membership card and
 * its billing summary on the right.
 *
 * The menu mirrors the native account screen's order, icons and grouping (`AccountScreen.tsx`), so
 * a member who holds both apps is not relearning the same screen twice. Two rows differ, and both
 * differ because the platform does:
 *
 *  - Notifications keeps a chevron here. On native it carries `arrow_outward` because it leaves for
 *    the OS Settings app, which is where notification permissions live; on web it is an ordinary
 *    in-app page.
 *  - "Elara Membership" exists here and NOT on native. Pointing at subscription management from
 *    an iOS binary is what triggers Apple's in-app-purchase requirement, which Elara does not want
 *    to take on — see `use-billing-portal.ts`.
 *
 * Billing has no page of its own. It is one card, one line of plan and one link — a page to hold
 * that would be a page the member has to find, and the thing they came for (when does this renew,
 * where do I change my card) is answered without the extra hop.
 *
 * On a narrow viewport the two columns stack, and the membership column goes FIRST — the same order
 * the native screen has, where the card is the top of the list. Source order is the other way round
 * because the menu leads on desktop, so the swap is done with `order` rather than by moving the
 * markup.
 */
export function AccountPage() {
  const profile = useCurrentUserProfile();
  const routerState = useRouterState();
  const sessionState = useSessionState();
  const userProfileState = useUserProfileState();
  const phoneLinkState = usePhoneLinkState();
  const billingPortal = useBillingPortal(profile?.subscription);

  if (!profile) {
    return (
      <AppLayout title={<AppLayoutTitle title="Account" />} transitionKey={routerState.currentRoute?.path}>
        <div className="flex items-center justify-center h-full">
          <Loader loading={true} />
        </div>
      </AppLayout>
    );
  }

  // Mirrors the native card's line exactly (`AccountScreen.tsx`), so a member who holds both sees
  // the same date in the same words.
  const memberSince = profile.createdAt ? formatDate(profile.createdAt, 'MMMM yyyy') : undefined;

  // Grouped as on native: the account itself, then the things around it, then the way out.
  const groups: AccountRow[][] = [
    [
      {
        kind: 'link',
        to: ROUTES.PROFILE,
        icon: 'person',
        label: 'Profile',
        callout: phoneLinkState.isVerified ? undefined : PROFILE_PHONE_CALLOUT,
      },
      {
        kind: 'link',
        to: ROUTES.TRAVEL_PREFERENCES,
        icon: 'travel',
        label: 'Travel Preferences',
        callout: travelPreferencesComplete(profile.travelerProfile) ? undefined : TRAVEL_PREFERENCES_CALLOUT,
      },
      { kind: 'link', to: ROUTES.FAMILY, icon: 'group', label: 'Family & Friends' },
      { kind: 'link', to: ROUTES.SECURITY, icon: 'lock', label: 'Security' },
      { kind: 'link', to: ROUTES.PAYMENT_METHODS, icon: 'credit_card', label: 'Payment Methods' },
      { kind: 'link', to: ROUTES.NOTIFICATIONS, icon: 'notifications', label: 'Notifications' },
      // The only row native does not have, so it has no native position to match — see the
      // header note for why it is web-only.
      ...(billingPortal.canManage
        ? [
            {
              kind: 'action' as const,
              icon: ELARA_MARK as RowIcon,
              label: 'Elara Membership',
              trailingIcon: (billingPortal.isOpening ? 'progress_activity' : 'arrow_outward') as IconName,
              disabled: billingPortal.isOpening,
              onClick: () => void billingPortal.openPortal(),
            },
          ]
        : []),
      { kind: 'link', to: ROUTES.SETTINGS, icon: 'settings', label: 'Settings' },
    ],
    [
      {
        kind: 'action',
        icon: 'share',
        label: 'Refer a Friend',
        // A dialog, not a destination — it stays in the app, so it keeps the chevron native gives it.
        trailingIcon: 'chevron_right',
        onClick: () => void userProfileState.openReferralDialog(),
      },
      { kind: 'external', href: TERMS_URL, icon: 'description', label: 'Terms of Service' },
      { kind: 'external', href: PRIVACY_URL, icon: 'shield', label: 'Privacy Policy' },
    ],
    [{ kind: 'action', icon: 'logout', label: 'Log Out', onClick: () => void sessionState.logout() }],
  ];

  return (
    <AppLayout title={<AppLayoutTitle title="Account" />} transitionKey={routerState.currentRoute?.path}>
      <div className="h-full flex flex-col overflow-hidden">
        <div className="flex-1 overflow-auto">
          <div className="max-w-4xl mx-auto w-full px-4 pt-[72px] pb-16 flex flex-col gap-6">
            <EditableAvatar profile={profile} />

            <PaymentFailedAlert />

            <div className="grid gap-10 md:grid-cols-[minmax(0,1fr)_20rem]">
              <nav className="order-2 md:order-1 flex flex-col">
                {groups.map((group, index) => (
                  <React.Fragment key={index}>
                    {index > 0 && <hr className="my-3" />}
                    {group.map((row) => (
                      <AccountMenuRow key={row.label} row={row} />
                    ))}
                  </React.Fragment>
                ))}
              </nav>

              <MembershipPanel
                className="order-1 md:order-2"
                name={profile.displayName || 'You'}
                memberSince={memberSince}
                subscription={profile.subscription}
              />
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
